import { BaseApiClient } from '@quantabit/sdk-config';
import { useState, useCallback, useEffect } from 'react';
import { jsx, jsxs } from 'react/jsx-runtime';

/**
 * OAuth SDK - API 客户端
 * OAuth 认证系统后端接口封装
 * 
 * 使用 BaseApiClient 基类简化代码
 */


/**
 * OAuth API 客户端
 */
class OauthApiClient extends BaseApiClient {
  constructor(config = {}) {
    super('/oauth', config);
  }
  _updateFromGlobalConfig(globalConfig) {
    if (!this._customBaseUrl) {
      let base = globalConfig.didApiUrl || '/did-api';
      const isAbsolute = /^https?:\/\//i.test(base);
      const isServer = typeof window === 'undefined';
      if (!isAbsolute && isServer) {
        const fullUrl = globalConfig.apiFullUrl || 'https://api.qbitchain.io';
        const cleanFull = String(fullUrl).replace(/\/+$/, '');
        const cleanBase = String(base).replace(/^\/+/, '');
        base = cleanFull && cleanBase ? `${cleanFull}/${cleanBase}` : cleanFull || `/${cleanBase}`;
      }
      const cleanBase = String(base).replace(/\/+$/, '');
      const cleanModule = String(this.modulePath).replace(/^\/+/, '');
      this.baseUrl = cleanBase && cleanModule ? `${cleanBase}/${cleanModule}` : cleanBase || `/${cleanModule}`;
    } else {
      this.baseUrl = String(this._customBaseUrl).replace(/\/+$/, '');
    }
    if (!this._customTimeout) {
      this.timeout = globalConfig.timeout || 30000;
    } else {
      this.timeout = this._customTimeout;
    }
  }
  _normalizeProviderArgs(providerOrOptions, options = {}) {
    if (typeof providerOrOptions === 'object' && providerOrOptions !== null) {
      const {
        provider,
        ...params
      } = providerOrOptions;
      return {
        provider,
        params
      };
    }
    return {
      provider: providerOrOptions,
      params: options
    };
  }
  _getSocialUrl(path) {
    const base = this.baseUrl.replace(/\/oauth$/, '/social');
    return `${base}${path}`;
  }

  // ============ OAuth 登录 ============

  /**
   * 获取 OAuth 登录 URL / 绑定 URL
   * @param {string} provider - 提供商（google, github, twitter, discord 等）
   * @param {Object} options - 选项
   */
  async getAuthUrl(provider, options = {}) {
    const normalized = this._normalizeProviderArgs(provider, options);
    const targetProvider = normalized.provider;
    const params = normalized.params;
    if (params.isLink) {
      // 绑定第三方账号，对应后端的 POST /social/bind/initiate
      const url = this._getSocialUrl('/bind/initiate');
      const requestUrl = `${url}?platform=${targetProvider}&verification_method=oauth`;
      const response = await this.post(requestUrl);
      const data = response.data || response;
      return {
        authUrl: data.oauth_url || data.data && data.data.oauth_url
      };
    }

    // 第三方登录/注册逻辑，对应后端的 GET /social/login/initiate
    const url = this._getSocialUrl('/login/initiate');
    const requestUrl = `${url}?platform=${targetProvider}`;
    const response = await this.get(requestUrl);
    const data = response.data || response;
    return {
      authUrl: data.oauth_url || data.data && data.data.oauth_url
    };
  }

  /**
   * OAuth 回调处理
   * @param {string} provider - 提供商
   * @param {string} code - 授权码
   * @param {string} state - 状态
   * @param {Object} options - 选项
   */
  async handleCallback(provider, code, state, options = {}) {
    let callbackProvider,
      callbackCode,
      callbackState,
      isLink = false;
    if (typeof provider === 'object' && provider !== null) {
      callbackProvider = provider.provider;
      callbackCode = provider.code;
      callbackState = provider.state;
      isLink = !!provider.isLink;
    } else {
      callbackProvider = provider;
      callbackCode = code;
      callbackState = state;
      isLink = !!options.isLink;
    }
    let url;
    if (isLink) {
      url = this._getSocialUrl('/bind/oauth/callback');
    } else {
      url = this._getSocialUrl('/login/oauth/callback');
    }
    const requestUrl = `${url}?platform=${callbackProvider}&code=${callbackCode}&state=${callbackState}`;
    const response = await this.post(requestUrl);
    const data = response.data || response;
    const resultData = data.data || data;
    return {
      user: {
        name: resultData.username || resultData.display_name || resultData.user && resultData.user.display_name || '用户'
      },
      ...resultData
    };
  }

  /**
   * 获取支持的 OAuth 提供商
   */
  async getProviders() {
    return {
      data: [{
        id: 'google',
        name: 'Google'
      }, {
        id: 'apple',
        name: 'Apple'
      }, {
        id: 'microsoft',
        name: 'Microsoft'
      }]
    };
  }

  // ============ 账号绑定 ============

  /**
   * 绑定第三方账号
   * @param {string} provider - 提供商
   * @param {string} code - 授权码
   */
  async bindAccount(provider, code) {
    const url = this._getSocialUrl('/bind/oauth/callback');
    const requestUrl = `${url}?platform=${provider}&code=${code}`;
    return this.post(requestUrl);
  }

  /**
   * 解绑第三方账号
   * @param {string} provider - 提供商
   */
  async unbindAccount(provider) {
    const url = this._getSocialUrl(`/unbind/${provider}`);
    return this.delete(url);
  }

  /**
   * 解绑第三方账号（兼容 hooks 命名）
   * @param {string} provider - 提供商
   */
  async unlinkAccount(provider) {
    return this.unbindAccount(provider);
  }

  /**
   * 获取已绑定账号
   */
  async getBoundAccounts() {
    const url = this._getSocialUrl('/bindings');
    const response = await this.get(url);
    const bindings = response.data?.bindings || response.bindings || [];
    const formatted = bindings.map(b => ({
      provider: b.platform,
      email: b.username || b.display_name || '已关联',
      ...b
    }));
    return {
      data: formatted
    };
  }

  /**
   * 获取已关联账号（兼容旧版快捷方法命名）
   */
  async getLinkedAccounts() {
    return this.getBoundAccounts();
  }

  /**
   * 检查账号绑定状态
   * @param {string} provider - 提供商
   */
  async checkBindStatus(provider) {
    const url = this._getSocialUrl(`/bindings/${provider}`);
    return this.get(url);
  }

  // ============ 钱包连接 ============

  /**
   * 获取钱包连接消息
   * @param {string} address - 钱包地址
   */
  async getWalletMessage(address) {
    return this.post('/wallet/message', {
      address
    });
  }

  /**
   * 验证钱包签名
   * @param {string} address - 钱包地址
   * @param {string} signature - 签名
   * @param {string} message - 消息
   */
  async verifyWalletSignature(address, signature, message) {
    return this.post('/wallet/verify', {
      address,
      signature,
      message
    });
  }

  /**
   * 使用钱包登录
   * @param {string} address - 钱包地址
   * @param {string} signature - 签名
   * @param {string} message - 消息
   */
  async loginWithWallet(address, signature, message) {
    return this.post('/wallet/login', {
      address,
      signature,
      message
    });
  }

  /**
   * 绑定钱包
   * @param {string} address - 钱包地址
   * @param {string} signature - 签名
   * @param {string} message - 消息
   */
  async bindWallet(address, signature, message) {
    return this.post('/wallet/bind', {
      address,
      signature,
      message
    });
  }

  /**
   * 解绑钱包
   * @param {string} address - 钱包地址
   */
  async unbindWallet(address) {
    return this.delete('/wallet/bind', {
      address
    });
  }

  /**
   * 获取已绑定钱包
   */
  async getBoundWallets() {
    return this.get('/wallet/list');
  }

  // ============ SSO 单点登录 ============

  /**
   * 获取 SSO 登录 URL
   * @param {Object} options - 选项
   */
  async getSSOUrl(options = {}) {
    return this.get('/sso/url', options);
  }

  /**
   * SSO 登录验证
   * @param {string} token - SSO Token
   */
  async verifySSOToken(token) {
    return this.post('/sso/verify', {
      token
    });
  }

  /**
   * SSO 登出
   */
  async ssoLogout() {
    return this.post('/sso/logout');
  }

  /**
   * 检查当前 OAuth 登录态
   */
  async checkAuth() {
    return this.get('/auth/check');
  }

  /**
   * 登出当前 OAuth 会话
   */
  async logout() {
    return this.post('/logout');
  }

  // ============ Token 管理 ============

  /**
   * 刷新 Token
   * @param {string} refreshToken - 刷新 Token
   */
  async refreshToken(refreshToken) {
    return this.post('/token/refresh', {
      refresh_token: refreshToken
    });
  }

  /**
   * 撤销 Token
   * @param {string} token - Token
   */
  async revokeToken(token) {
    return this.post('/token/revoke', {
      token
    });
  }

  /**
   * 获取 Token 信息
   * @param {string} token - Token
   */
  async getTokenInfo(token) {
    return this.post('/token/info', {
      token
    });
  }

  // ============ 授权管理 ============

  /**
   * 获取授权应用列表
   */
  async getAuthorizedApps() {
    return this.get('/apps');
  }

  /**
   * 撤销应用授权
   * @param {string} appId - 应用 ID
   */
  async revokeAppAuthorization(appId) {
    return this.delete(`/apps/${appId}`);
  }
}

// 创建默认实例
const oauthApi = new OauthApiClient();
const OAuthApiClient = OauthApiClient;

/**
 * OAuth SDK - 类型定义
 */

// 第三方登录提供商
const OAuthProvider = {
  // 社交
  GOOGLE: 'google',
  FACEBOOK: 'facebook',
  TWITTER: 'twitter',
  GITHUB: 'github',
  DISCORD: 'discord',
  TELEGRAM: 'telegram',
  WECHAT: 'wechat',
  TIKTOK: 'tiktok',
  WEIBO: 'weibo',
  QQ: 'qq',
  APPLE: 'apple',
  // 钱包
  METAMASK: 'metamask',
  WALLET_CONNECT: 'wallet_connect',
  COINBASE_WALLET: 'coinbase_wallet',
  PHANTOM: 'phantom',
  // 企业
  MICROSOFT: 'microsoft',
  OKTA: 'okta',
  AUTH0: 'auth0'
};

// 连接状态
const ConnectionStatus = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  ERROR: 'error'
};

// 账户绑定状态
const BindingStatus = {
  NOT_BOUND: 'not_bound',
  BOUND: 'bound',
  PENDING: 'pending'
};

// 授权范围
const OAuthScope = {
  PROFILE: 'profile',
  EMAIL: 'email',
  OPENID: 'openid',
  READ: 'read',
  WRITE: 'write'
};

/**
 * OAuth SDK - 国际化
 * OAuth授权系统多语言支持
 */

const SUPPORTED_LANGUAGES = ['en', 'zh', 'ja', 'ko'];
const messages = {
  zh: {
    // 登录
    login: '登录',
    loginWith: '使用 {provider} 登录',
    loginTo: '登录到',
    signIn: '登录',
    signUp: '注册',
    signOut: '退出登录',
    logout: '退出',
    // 第三方
    google: 'Google',
    apple: 'Apple',
    github: 'GitHub',
    microsoft: '微软',
    twitter: 'Twitter',
    facebook: 'Facebook',
    discord: 'Discord',
    wechat: '微信',
    tiktok: 'TikTok',
    qq: 'QQ',
    // 授权
    authorize: '授权',
    authorization: '授权',
    authorizing: '授权中...',
    allowAccess: '允许访问',
    denyAccess: '拒绝访问',
    // 权限
    permissions: '权限',
    requestingPermissions: '请求以下权限',
    readProfile: '读取基本资料',
    readEmail: '读取邮箱地址',
    writeData: '写入数据',
    // 连接
    connect: '连接',
    connected: '已连接',
    disconnect: '断开连接',
    connectionRequired: '需要连接账户',
    // 状态
    loading: '加载中...',
    redirecting: '跳转中...',
    success: '授权成功',
    failed: '授权失败',
    error: '发生错误',
    tryAgain: '重试',
    // 账户
    linkedAccounts: '已关联账户',
    linkAccount: '关联账户',
    unlinkAccount: '解除关联',
    noLinkedAccounts: '暂无关联账户',
    // 安全
    secureLogin: '安全登录',
    securedBy: '由 {provider} 提供安全保障',
    // 协议
    termsOfService: '服务条款',
    privacyPolicy: '隐私政策',
    byLoggingIn: '登录即表示您同意我们的',
    and: '和',
    // 返回
    cancel: '取消',
    back: '返回',
    continueAs: '以 {name} 继续'
  },
  en: {
    login: 'Login',
    loginWith: 'Login with {provider}',
    loginTo: 'Login to',
    signIn: 'Sign In',
    signUp: 'Sign Up',
    signOut: 'Sign Out',
    logout: 'Logout',
    google: 'Google',
    apple: 'Apple',
    github: 'GitHub',
    microsoft: 'Microsoft',
    twitter: 'Twitter',
    facebook: 'Facebook',
    discord: 'Discord',
    wechat: 'WeChat',
    tiktok: 'TikTok',
    qq: 'QQ',
    authorize: 'Authorize',
    authorization: 'Authorization',
    authorizing: 'Authorizing...',
    allowAccess: 'Allow Access',
    denyAccess: 'Deny Access',
    permissions: 'Permissions',
    requestingPermissions: 'Requesting the following permissions',
    readProfile: 'Read your profile',
    readEmail: 'Read your email',
    writeData: 'Write data',
    connect: 'Connect',
    connected: 'Connected',
    disconnect: 'Disconnect',
    connectionRequired: 'Connection required',
    loading: 'Loading...',
    redirecting: 'Redirecting...',
    success: 'Success',
    failed: 'Failed',
    error: 'Error',
    tryAgain: 'Try Again',
    linkedAccounts: 'Linked Accounts',
    linkAccount: 'Link Account',
    unlinkAccount: 'Unlink',
    noLinkedAccounts: 'No linked accounts',
    secureLogin: 'Secure Login',
    securedBy: 'Secured by {provider}',
    termsOfService: 'Terms of Service',
    privacyPolicy: 'Privacy Policy',
    byLoggingIn: 'By logging in, you agree to our',
    and: 'and',
    cancel: 'Cancel',
    back: 'Back',
    continueAs: 'Continue as {name}'
  },
  ja: {
    login: 'ログイン',
    loginWith: '{provider} でログイン',
    loginTo: 'ログイン先',
    signIn: 'サインイン',
    signUp: 'サインアップ',
    signOut: 'サインアウト',
    logout: 'ログアウト',
    google: 'Google',
    apple: 'Apple',
    github: 'GitHub',
    microsoft: 'Microsoft',
    twitter: 'Twitter',
    facebook: 'Facebook',
    discord: 'Discord',
    wechat: 'WeChat',
    tiktok: 'TikTok',
    qq: 'QQ',
    authorize: '認可',
    authorization: '認可',
    authorizing: '認可中...',
    allowAccess: 'アクセスを許可',
    denyAccess: 'アクセスを拒否',
    permissions: '権限',
    requestingPermissions: '以下の権限を要求しています',
    readProfile: 'プロフィールの読み取り',
    readEmail: 'メールの読み取り',
    writeData: 'データの書き込み',
    connect: '接続',
    connected: '接続済み',
    disconnect: '切断',
    connectionRequired: '接続が必要',
    loading: '読み込み中...',
    redirecting: 'リダイレクト中...',
    success: '成功',
    failed: '失敗',
    error: 'エラー',
    tryAgain: '再試行',
    linkedAccounts: '連携済みアカウント',
    linkAccount: 'アカウントを連携',
    unlinkAccount: '連携解除',
    noLinkedAccounts: '連携アカウントなし',
    secureLogin: 'セキュアログイン',
    securedBy: '{provider} によるセキュリティ',
    termsOfService: '利用規約',
    privacyPolicy: 'プライバシーポリシー',
    byLoggingIn: 'ログインすると、以下に同意することになります：',
    and: 'と',
    cancel: 'キャンセル',
    back: '戻る',
    continueAs: '{name} として続行'
  },
  ko: {
    login: '로그인',
    loginWith: '{provider}로 로그인',
    loginTo: '로그인 대상',
    signIn: '로그인',
    signUp: '가입하기',
    signOut: '로그아웃',
    logout: '로그아웃',
    google: 'Google',
    apple: 'Apple',
    github: 'GitHub',
    microsoft: 'Microsoft',
    twitter: 'Twitter',
    facebook: 'Facebook',
    discord: 'Discord',
    wechat: 'WeChat',
    tiktok: 'TikTok',
    qq: 'QQ',
    authorize: '승인',
    authorization: '승인',
    authorizing: '승인 중...',
    allowAccess: '접근 허용',
    denyAccess: '접근 거부',
    permissions: '권한',
    requestingPermissions: '다음 권한을 요청합니다',
    readProfile: '프로필 읽기',
    readEmail: '이메일 읽기',
    writeData: '데이터 쓰기',
    connect: '연결',
    connected: '연결됨',
    disconnect: '연결 해제',
    connectionRequired: '연결 필요',
    loading: '로딩 중...',
    redirecting: '리다이렉트 중...',
    success: '성공',
    failed: '실패',
    error: '오류',
    tryAgain: '다시 시도',
    linkedAccounts: '연결된 계정',
    linkAccount: '계정 연결',
    unlinkAccount: '연결 해제',
    noLinkedAccounts: '연결된 계정 없음',
    secureLogin: '보안 로그인',
    securedBy: '{provider}에 의해 보안됨',
    termsOfService: '서비스 약관',
    privacyPolicy: '개인정보 처리방침',
    byLoggingIn: '로그인하면 다음에 동의하는 것입니다:',
    and: '및',
    cancel: '취소',
    back: '뒤로',
    continueAs: '{name}(으)로 계속'
  }
};
let currentLanguage = 'zh';
function setLanguage(lang) {
  if (SUPPORTED_LANGUAGES.includes(lang)) currentLanguage = lang;
}
function getLanguage() {
  return currentLanguage;
}
function t(key, params = {}) {
  let text = (messages[currentLanguage] || messages.en)[key] || key;
  Object.entries(params).forEach(([k, v]) => {
    text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
  });
  return text;
}

/**
 * OAuth SDK - React Hooks
 * OAuth授权系统相关的状态管理
 */


/**
 * OAuth登录
 */
function useOAuthLogin() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const login = useCallback(async (provider, options = {}) => {
    try {
      setLoading(true);
      setError(null);
      const {
        redirectUri,
        scope,
        state
      } = options;

      // 获取授权URL
      const response = await oauthApi.getAuthUrl({
        provider,
        redirectUri: redirectUri || window.location.origin + '/oauth/callback',
        scope,
        state
      });

      // 打开授权窗口或跳转
      if (options.popup) {
        return openPopup(response.authUrl, provider);
      } else {
        window.location.href = response.authUrl;
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  return {
    login,
    loading,
    error
  };
}

/**
 * OAuth回调处理
 */
function useOAuthCallback() {
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const handleCallback = useCallback(async (code, state, provider) => {
    try {
      setStatus('loading');
      setError(null);
      const response = await oauthApi.handleCallback({
        code,
        state,
        provider
      });
      setUser(response.user);
      setResult(response);
      setStatus('success');
      return response;
    } catch (err) {
      setError(err.message);
      setStatus('error');
      throw err;
    }
  }, []);

  // 自动处理URL参数
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const provider = params.get('provider');
    if (code) {
      handleCallback(code, state, provider);
    }
  }, [handleCallback]);
  return {
    status,
    user,
    error,
    result,
    handleCallback
  };
}

/**
 * 已关联账户
 */
function useLinkedAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await oauthApi.getLinkedAccounts();
      setAccounts(response.data || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);
  const linkAccount = useCallback(async provider => {
    const response = await oauthApi.getAuthUrl({
      provider,
      redirectUri: window.location.origin + '/oauth/link-callback',
      isLink: true
    });
    window.location.href = response.authUrl;
  }, []);
  const unlinkAccount = useCallback(async provider => {
    try {
      await oauthApi.unlinkAccount(provider);
      setAccounts(prev => prev.filter(a => a.provider !== provider));
    } catch (err) {
      console.error('Unlink error:', err);
      throw err;
    }
  }, []);
  const isLinked = useCallback(provider => {
    return accounts.some(a => a.provider === provider);
  }, [accounts]);
  return {
    accounts,
    loading,
    error,
    linkAccount,
    unlinkAccount,
    isLinked,
    refresh: fetchAccounts
  };
}

/**
 * OAuth状态
 */
function useOAuthState() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const checkAuth = useCallback(async () => {
    try {
      setLoading(true);
      const response = await oauthApi.checkAuth();
      setIsAuthenticated(response.isAuthenticated);
      setUser(response.user);
    } catch (err) {
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);
  const logout = useCallback(async () => {
    try {
      await oauthApi.logout();
      setIsAuthenticated(false);
      setUser(null);
    } catch (err) {
      console.error('Logout error:', err);
    }
  }, []);
  return {
    isAuthenticated,
    user,
    loading,
    logout,
    refresh: checkAuth
  };
}

/**
 * 可用的OAuth提供商
 */
function useOAuthProviders() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchProviders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await oauthApi.getProviders();
      setProviders(response.data || []);
    } catch (err) {
      console.error('Get providers error:', err);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);
  return {
    providers,
    loading
  };
}

/**
 * 弹窗登录
 */
function openPopup(url, name) {
  const width = 500;
  const height = 600;
  const left = (window.innerWidth - width) / 2 + window.screenX;
  const top = (window.innerHeight - height) / 2 + window.screenY;
  const popup = window.open(url, name, `width=${width},height=${height},left=${left},top=${top}`);
  return new Promise((resolve, reject) => {
    const checkClosed = setInterval(() => {
      if (!popup || popup.closed) {
        clearInterval(checkClosed);
        reject(new Error('Login window closed'));
      }
    }, 500);

    // 监听消息
    const handleMessage = event => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'oauth_callback') {
        clearInterval(checkClosed);
        window.removeEventListener('message', handleMessage);
        popup?.close();
        if (event.data.error) {
          reject(new Error(event.data.error));
        } else {
          resolve(event.data.result);
        }
      }
    };
    window.addEventListener('message', handleMessage);
  });
}

/**
 * OAuth SDK - React 组件
 * OAuth授权系统可视化组件
 */

const GoogleIcon = /*#__PURE__*/jsxs("svg", {
  viewBox: "0 0 24 24",
  width: "18",
  height: "18",
  style: {
    display: 'inline-block',
    verticalAlign: 'middle'
  },
  children: [/*#__PURE__*/jsx("path", {
    fill: "#4285F4",
    d: "M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
  }), /*#__PURE__*/jsx("path", {
    fill: "#34A853",
    d: "M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
  }), /*#__PURE__*/jsx("path", {
    fill: "#FBBC05",
    d: "M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
  }), /*#__PURE__*/jsx("path", {
    fill: "#EA4335",
    d: "M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
  })]
});
const WeChatIcon = /*#__PURE__*/jsx("svg", {
  viewBox: "0 0 24 24",
  width: "18",
  height: "18",
  fill: "currentColor",
  style: {
    display: 'inline-block',
    verticalAlign: 'middle'
  },
  children: /*#__PURE__*/jsx("path", {
    d: "M8.28 2c-3.844 0-6.963 2.55-6.963 5.7 0 1.776 1.025 3.376 2.662 4.453l-.68 2.457 2.518-1.258c.783.21 1.616.326 2.463.326.31 0 .614-.02.915-.054-.266-.757-.41-1.573-.41-2.424 0-3.693 3.298-6.686 7.367-6.686.347 0 .69.022 1.027.065C16.143 3.253 12.52 2 8.28 2zm10.073 6.963c-3.155 0-5.713 2.186-5.713 4.882 0 2.697 2.558 4.882 5.713 4.882.69 0 1.354-.1 1.979-.278l2.062 1.03-.556-2.015c1.341-.92 2.228-2.288 2.228-3.619 0-2.696-2.558-4.882-5.713-4.882z"
  })
});
const TikTokIcon = /*#__PURE__*/jsx("svg", {
  viewBox: "0 0 24 24",
  width: "18",
  height: "18",
  fill: "currentColor",
  style: {
    display: 'inline-block',
    verticalAlign: 'middle'
  },
  children: /*#__PURE__*/jsx("path", {
    d: "M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12.98 2.63 1.5 4.13 1.63v3.9c-1.4-.04-2.8-.44-3.97-1.24-.26-.17-.5-.36-.73-.56-.02 2.87.01 5.73-.02 8.6-.08 2.54-.97 5.06-2.88 6.74-2.02 1.76-4.91 2.39-7.46 1.71-2.92-.76-5.32-3.23-5.88-6.22-.65-3.48 1.04-7.22 4.19-8.79.87-.43 1.83-.69 2.8-.76V9.67c-1.39.11-2.78.85-3.52 2.03-.89 1.4-.87 3.39.05 4.74.88 1.3 2.5 2.05 4.07 1.82 1.63-.23 3.01-1.63 3.16-3.27.05-1.57.02-3.15.03-4.73V.02h-.47z"
  })
});
const AppleIcon = /*#__PURE__*/jsx("svg", {
  viewBox: "0 0 24 24",
  width: "18",
  height: "18",
  fill: "currentColor",
  style: {
    display: 'inline-block',
    verticalAlign: 'middle'
  },
  children: /*#__PURE__*/jsx("path", {
    d: "M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 4.17c.66-.81 1.11-1.93.99-3.06-.96.04-2.13.64-2.82 1.45-.6.69-1.12 1.83-.98 2.94 1.08.08 2.15-.52 2.81-1.33"
  })
});
const GithubIcon = /*#__PURE__*/jsx("svg", {
  viewBox: "0 0 24 24",
  width: "18",
  height: "18",
  fill: "currentColor",
  style: {
    display: 'inline-block',
    verticalAlign: 'middle'
  },
  children: /*#__PURE__*/jsx("path", {
    d: "M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.831.092-.646.35-1.086.636-1.336-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.579.688.481C19.137 20.162 22 16.418 22 12c0-5.523-4.477-10-10-10z"
  })
});
const MicrosoftIcon = /*#__PURE__*/jsxs("svg", {
  viewBox: "0 0 23 23",
  width: "18",
  height: "18",
  style: {
    display: 'inline-block',
    verticalAlign: 'middle'
  },
  children: [/*#__PURE__*/jsx("path", {
    fill: "#f25022",
    d: "M0 0h11v11H0z"
  }), /*#__PURE__*/jsx("path", {
    fill: "#7fba00",
    d: "M12 0h11v11H12z"
  }), /*#__PURE__*/jsx("path", {
    fill: "#00a4ef",
    d: "M0 12h11v11H0z"
  }), /*#__PURE__*/jsx("path", {
    fill: "#ffb900",
    d: "M12 12h11v11H12z"
  })]
});

// 提供商图标
const providerIcons = {
  google: GoogleIcon,
  apple: AppleIcon,
  github: GithubIcon,
  microsoft: MicrosoftIcon,
  wechat: WeChatIcon,
  tiktok: TikTokIcon
};

// 提供商品牌色
const providerColors = {
  google: '#ffffff',
  // Google 官方底色常为白色
  apple: '#000000',
  github: '#24292e',
  microsoft: '#ffffff',
  twitter: '#1da1f2',
  facebook: '#1877f2',
  discord: '#5865f2',
  wechat: '#07c160',
  tiktok: '#000000',
  qq: '#12b7f5'
};

/**
 * 第三方登录按钮
 */
function OAuthButton({
  provider,
  variant = 'default',
  onSuccess,
  onError,
  onClick
}) {
  const {
    login,
    loading
  } = useOAuthLogin();
  const handleClick = useCallback(async e => {
    if (onClick) {
      onClick(e);
      return;
    }
    try {
      const result = await login(provider, {
        popup: true
      });
      onSuccess?.(result);
    } catch (err) {
      onError?.(err);
    }
  }, [login, provider, onSuccess, onError, onClick]);
  const icon = providerIcons[provider] || '🔐';
  const color = providerColors[provider];
  return /*#__PURE__*/jsxs("button", {
    className: `eco-oauth-btn eco-oauth-btn-${variant} eco-oauth-btn-${provider}`,
    onClick: handleClick,
    disabled: loading,
    style: variant === 'brand' ? {
      backgroundColor: color
    } : undefined,
    children: [/*#__PURE__*/jsx("span", {
      className: "eco-oauth-btn-icon",
      children: icon
    }), /*#__PURE__*/jsx("span", {
      className: "eco-oauth-btn-text",
      children: loading ? t('loading') : t('loginWith', {
        provider: t(provider)
      })
    })]
  });
}

/**
 * 登录按钮组
 */
function OAuthButtonGroup({
  providers = ['google', 'apple', 'microsoft'],
  variant = 'default',
  onSuccess,
  onError
}) {
  return /*#__PURE__*/jsx("div", {
    className: "eco-oauth-btn-group",
    children: providers.map(provider => /*#__PURE__*/jsx(OAuthButton, {
      provider: provider,
      variant: variant,
      onSuccess: onSuccess,
      onError: onError
    }, provider))
  });
}

/**
 * 图标按钮组
 */
function OAuthIconGroup({
  providers = ['google', 'apple', 'microsoft'],
  onSuccess,
  onError
}) {
  const {
    login,
    loading
  } = useOAuthLogin();
  const handleClick = useCallback(async provider => {
    try {
      const result = await login(provider, {
        popup: true
      });
      onSuccess?.(result);
    } catch (err) {
      onError?.(err);
    }
  }, [login, onSuccess, onError]);
  return /*#__PURE__*/jsx("div", {
    className: "eco-oauth-icon-group",
    children: providers.map(provider => /*#__PURE__*/jsx("button", {
      className: `eco-oauth-icon-btn eco-oauth-icon-${provider}`,
      onClick: () => handleClick(provider),
      disabled: loading,
      title: t(provider),
      style: {
        backgroundColor: providerColors[provider]
      },
      children: providerIcons[provider] || '🔐'
    }, provider))
  });
}

/**
 * OAuth回调页面
 */
function OAuthCallbackPage({
  onSuccess,
  onError
}) {
  const {
    status,
    user,
    error,
    result
  } = useOAuthCallback();
  if (status === 'loading') {
    return /*#__PURE__*/jsx("div", {
      className: "eco-oauth-callback",
      children: /*#__PURE__*/jsxs("div", {
        className: "eco-oauth-callback-loading",
        children: [/*#__PURE__*/jsx("div", {
          className: "eco-oauth-spinner"
        }), /*#__PURE__*/jsx("span", {
          children: t('authorizing')
        })]
      })
    });
  }
  if (status === 'error') {
    onError?.(error);
    return /*#__PURE__*/jsx("div", {
      className: "eco-oauth-callback",
      children: /*#__PURE__*/jsxs("div", {
        className: "eco-oauth-callback-error",
        children: [/*#__PURE__*/jsx("span", {
          className: "eco-oauth-callback-icon",
          children: "\u274C"
        }), /*#__PURE__*/jsx("h3", {
          children: t('failed')
        }), /*#__PURE__*/jsx("p", {
          children: error
        }), /*#__PURE__*/jsx("button", {
          onClick: () => window.close(),
          children: t('back')
        })]
      })
    });
  }
  if (status === 'success') {
    onSuccess?.(result);

    // 如果在弹窗中，发送消息给父窗口
    if (window.opener) {
      window.opener.postMessage({
        type: 'oauth_callback',
        result: result
      }, window.location.origin);
    }
    return /*#__PURE__*/jsx("div", {
      className: "eco-oauth-callback",
      children: /*#__PURE__*/jsxs("div", {
        className: "eco-oauth-callback-success",
        children: [/*#__PURE__*/jsx("span", {
          className: "eco-oauth-callback-icon",
          children: "\u2705"
        }), /*#__PURE__*/jsx("h3", {
          children: t('success')
        }), /*#__PURE__*/jsx("p", {
          children: t('continueAs', {
            name: user?.display_name || user?.name
          })
        })]
      })
    });
  }
  return null;
}

/**
 * 已关联账户列表
 */
function LinkedAccountsList() {
  const {
    accounts,
    loading,
    unlinkAccount,
    isLinked,
    linkAccount
  } = useLinkedAccounts();
  const {
    providers
  } = useOAuthProviders();
  if (loading) {
    return /*#__PURE__*/jsx("div", {
      className: "eco-oauth-linked eco-oauth-loading",
      children: /*#__PURE__*/jsx("div", {
        className: "eco-oauth-spinner"
      })
    });
  }
  return /*#__PURE__*/jsxs("div", {
    className: "eco-oauth-linked",
    children: [/*#__PURE__*/jsx("h4", {
      children: t('linkedAccounts')
    }), accounts.length === 0 ? /*#__PURE__*/jsx("div", {
      className: "eco-oauth-linked-empty",
      children: t('noLinkedAccounts')
    }) : /*#__PURE__*/jsx("div", {
      className: "eco-oauth-linked-list",
      children: accounts.map(account => /*#__PURE__*/jsxs("div", {
        className: "eco-oauth-linked-item",
        children: [/*#__PURE__*/jsx("span", {
          className: "eco-oauth-linked-icon",
          style: {
            backgroundColor: providerColors[account.provider]
          },
          children: providerIcons[account.provider]
        }), /*#__PURE__*/jsxs("div", {
          className: "eco-oauth-linked-info",
          children: [/*#__PURE__*/jsx("span", {
            className: "eco-oauth-linked-name",
            children: t(account.provider)
          }), /*#__PURE__*/jsx("span", {
            className: "eco-oauth-linked-email",
            children: account.email
          })]
        }), /*#__PURE__*/jsx("button", {
          className: "eco-oauth-unlink-btn",
          onClick: () => unlinkAccount(account.provider),
          children: t('unlinkAccount')
        })]
      }, account.provider))
    }), /*#__PURE__*/jsxs("div", {
      className: "eco-oauth-link-more",
      children: [/*#__PURE__*/jsx("h5", {
        children: t('linkAccount')
      }), /*#__PURE__*/jsx("div", {
        className: "eco-oauth-link-options",
        children: providers.filter(p => !isLinked(p.id)).map(p => /*#__PURE__*/jsx(OAuthButton, {
          provider: p.id,
          variant: "outline",
          onClick: () => linkAccount(p.id)
        }, p.id))
      })]
    })]
  });
}

/**
 * 登录模态框
 */
function OAuthLoginModal({
  isOpen,
  onClose,
  onSuccess
}) {
  if (!isOpen) return null;
  return /*#__PURE__*/jsxs("div", {
    className: "eco-oauth-modal",
    children: [/*#__PURE__*/jsx("div", {
      className: "eco-oauth-modal-overlay",
      onClick: onClose
    }), /*#__PURE__*/jsxs("div", {
      className: "eco-oauth-modal-content",
      children: [/*#__PURE__*/jsx("button", {
        className: "eco-oauth-modal-close",
        onClick: onClose,
        children: "\xD7"
      }), /*#__PURE__*/jsx("h2", {
        children: t('login')
      }), /*#__PURE__*/jsx(OAuthButtonGroup, {
        providers: ['google', 'apple', 'microsoft'],
        variant: "brand",
        onSuccess: result => {
          onSuccess?.(result);
          onClose();
        }
      }), /*#__PURE__*/jsx("div", {
        className: "eco-oauth-modal-divider",
        children: /*#__PURE__*/jsx("span", {
          children: "\u6216"
        })
      }), /*#__PURE__*/jsx(OAuthIconGroup, {
        providers: ['facebook', 'discord', 'twitter'],
        onSuccess: result => {
          onSuccess?.(result);
          onClose();
        }
      }), /*#__PURE__*/jsxs("div", {
        className: "eco-oauth-modal-footer",
        children: [/*#__PURE__*/jsx("span", {
          children: t('byLoggingIn')
        }), /*#__PURE__*/jsx("a", {
          href: "/terms",
          children: t('termsOfService')
        }), /*#__PURE__*/jsx("span", {
          children: t('and')
        }), /*#__PURE__*/jsx("a", {
          href: "/privacy",
          children: t('privacyPolicy')
        })]
      })]
    })]
  });
}

/**
 * @quantabit/oauth-sdk
 * OAuth System SDK - Full Version
 */

const getAuthUrl = (...args) => oauthApi.getAuthUrl(...args);
const handleCallback = (...args) => oauthApi.handleCallback(...args);
const checkAuth = (...args) => oauthApi.checkAuth(...args);
const logout = (...args) => oauthApi.logout(...args);
const getLinkedAccounts = (...args) => oauthApi.getLinkedAccounts(...args);

export { BindingStatus, ConnectionStatus, LinkedAccountsList, OAuthApiClient, OAuthButton, OAuthButtonGroup, OAuthCallbackPage, OAuthIconGroup, OAuthLoginModal, OAuthProvider, OAuthScope, SUPPORTED_LANGUAGES, checkAuth, getAuthUrl, getLanguage, getLinkedAccounts, handleCallback, logout, messages, oauthApi, setLanguage, t, useLinkedAccounts, useOAuthCallback, useOAuthLogin, useOAuthProviders, useOAuthState };
//# sourceMappingURL=index.esm.js.map
